import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authMe, authLogin, authRegister, authLogout, getEntitlements } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking, null = anon, object = authed
  const [entitlements, setEntitlements] = useState(null);

  const refreshEntitlements = useCallback(async () => {
    try {
      const e = await getEntitlements();
      setEntitlements(e);
      return e;
    } catch (_) {
      setEntitlements(null);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const u = await authMe();
      setUser(u);
      refreshEntitlements();
      return u;
    } catch (_) {
      setUser(null);
      setEntitlements(null);
      return null;
    }
  }, [refreshEntitlements]);

  useEffect(() => {
    // If returning from Google OAuth, let AuthCallback exchange the session first.
    if (window.location.hash?.includes("session_id=")) {
      setUser(null);
      return;
    }
    refreshUser();
  }, [refreshUser]);

  const login = async (email, password) => {
    await authLogin({ email, password });
    return await refreshUser();
  };

  const register = async (email, password, name, invite_token) => {
    await authRegister({ email, password, name, invite_token });
    return await refreshUser();
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch (_) {
      /* ignore */
    }
    setUser(null);
    setEntitlements(null);
  };

  const isPro = entitlements ? entitlements.pro : user?.workspace?.plan === "pro";

  return (
    <AuthContext.Provider value={{ user, setUser, refreshUser, login, register, logout, entitlements, refreshEntitlements, isPro }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
